export interface BrowserInfo {
  browserName: string;
  browserVersion: string;
  screenResolution: string;
}

export interface SystemOsInfo {
  systemOS: string;
  systemOSVersion: string;
}

// Order matters — Edge/Opera also match "Chrome", and Chrome also matches Safari's pattern.
const BROWSER_PATTERNS: ReadonlyArray<readonly [string, RegExp]> = [
  ['Edge', /Edg\/([\d.]+)/],
  ['Opera', /OPR\/([\d.]+)/],
  ['Chrome', /Chrome\/([\d.]+)/],
  ['Firefox', /Firefox\/([\d.]+)/],
  // Version/ is checked first; Safari presence is validated in parseVersionFromUserAgent.
  ['Safari', /Version\/([\d.]+)/],
  ['Internet Explorer', /MSIE ([\d.]+)/],
  ['Internet Explorer', /rv:([\d.]+)/],
];

// Order matters — check mobile OSes before their desktop-lookalike substrings.
const OS_PATTERNS: ReadonlyArray<readonly [string, RegExp]> = [
  ['Android', /Android ([\d.]+)/],
  ['iOS', /(?:iPhone|iPad|iPod).*?OS ([\d_]+)/],
  ['Windows', /Windows NT ([\d.]+)/],
  ['macOS', /Mac OS X ([\d._]+)/],
  ['Chrome OS', /CrOS \S+ ([\d.]+)/],
  ['Linux', /Linux/],
];

interface UaDataBrandVersion {
  brand: string;
  version: string;
}

interface NavigatorUaData {
  /** Low-entropy — synchronously available without a permission-style prompt. */
  platform?: string;
  getHighEntropyValues: (hints: Array<string>) => Promise<{
    fullVersionList?: Array<UaDataBrandVersion>;
    uaFullVersion?: string;
    platformVersion?: string;
  }>;
}

function parseVersionFromUserAgent(userAgent: string): {
  browserName: string;
  browserVersion: string;
} {
  for (const [name, pattern] of BROWSER_PATTERNS) {
    const match = pattern.exec(userAgent);
    if (!match) {
      continue;
    }
    // Preserve previous `/Version\/...Safari/` behavior without a backtracking regex.
    if (name === 'Safari' && !userAgent.includes('Safari')) {
      continue;
    }
    return { browserName: name, browserVersion: match[1] ?? 'Unknown' };
  }

  return { browserName: 'Unknown', browserVersion: 'Unknown' };
}

function parseOsFromUserAgent(userAgent: string): SystemOsInfo {
  for (const [name, pattern] of OS_PATTERNS) {
    const match = pattern.exec(userAgent);
    if (match) {
      return {
        systemOS: name,
        systemOSVersion: (match[1] ?? 'Unknown').replaceAll('_', '.'),
      };
    }
  }

  return { systemOS: 'Unknown', systemOSVersion: 'Unknown' };
}

/**
 * Chromium does not expose Windows's real marketing version through `platformVersion`
 * — it encodes the OS generation in the major segment instead (see Chromium's
 * "Detecting Windows 11 with User-Agent Client Hints" origin trial): major `0` means
 * legacy Windows 7/8/8.1 (minor `1`/`2`/`3`), `1`-`12` means Windows 10, `13`+ means
 * Windows 11. A raw `platformVersion` like "19.0.0" is genuinely how Chromium says
 * "Windows 11" — this translates it into something a human can actually read.
 */
export function mapWindowsPlatformVersion(platformVersion: string): string {
  const [majorText, minorText] = platformVersion.split('.');
  const major = Number.parseInt(majorText ?? '', 10);

  if (Number.isNaN(major)) {
    return platformVersion;
  }

  if (major === 0) {
    const minor = Number.parseInt(minorText ?? '', 10);
    if (minor === 1) return '7';
    if (minor === 2) return '8';
    if (minor === 3) return '8.1';
    return platformVersion;
  }

  return major >= 13 ? '11' : '10';
}

function isRealBrand(brand: string): boolean {
  // Chromium sends a deliberately fake "greasy" brand entry (e.g. "Not_A Brand")
  // to discourage sniffing on the brand list itself — skip it when picking a version.
  return !/not.*brand/i.test(brand);
}

/**
 * Chrome (and other Chromium browsers) report a "reduced" UA string since v110 —
 * `Chrome/150.0.0.0` instead of the real `150.0.7871.101`, and a frozen OS version
 * (e.g. `Windows NT 10.0` for both Windows 10 and 11) — for privacy reasons. The
 * real values are only available via the async User-Agent Client Hints API, so we
 * kick that lookup off once at module load and cache the result; getBrowserInfo()
 * / getSystemOsInfo() stay synchronous and just use whatever's available by then.
 */
let cachedFullVersion: string | null = null;
let cachedOsVersion: string | null = null;

const uaData = (
  globalThis.navigator as
    | (Navigator & { userAgentData?: NavigatorUaData })
    | undefined
)?.userAgentData;

// Not top-level await — CRA's default build target doesn't reliably support it, and this
// is a best-effort cache warm-up that must never block module evaluation either way.
/* eslint-disable unicorn/prefer-top-level-await */
uaData
  ?.getHighEntropyValues([
    'fullVersionList',
    'uaFullVersion',
    'platformVersion',
  ])
  .then((result) => {
    const fullVersion =
      result.fullVersionList?.find((entry) => isRealBrand(entry.brand))
        ?.version ?? result.uaFullVersion;
    if (fullVersion) {
      cachedFullVersion = fullVersion;
    }
    if (result.platformVersion) {
      cachedOsVersion = result.platformVersion;
    }
  })
  .catch(() => {
    // Best effort — getBrowserInfo()/getSystemOsInfo() fall back to the UA string.
  });
/* eslint-enable unicorn/prefer-top-level-await */

export function getBrowserInfo(): BrowserInfo {
  const userAgent = globalThis.navigator?.userAgent ?? '';
  const { browserName, browserVersion } = parseVersionFromUserAgent(userAgent);

  const screenResolution = globalThis.screen
    ? `${globalThis.screen.width}x${globalThis.screen.height}`
    : 'unknown';

  return {
    browserName,
    browserVersion: cachedFullVersion ?? browserVersion,
    screenResolution,
  };
}

export function getSystemOsInfo(): SystemOsInfo {
  const userAgent = globalThis.navigator?.userAgent ?? '';
  const fallback = parseOsFromUserAgent(userAgent);
  const systemOS = uaData?.platform || fallback.systemOS;

  if (!cachedOsVersion) {
    return { systemOS, systemOSVersion: fallback.systemOSVersion };
  }

  const systemOSVersion =
    systemOS === 'Windows'
      ? mapWindowsPlatformVersion(cachedOsVersion)
      : cachedOsVersion;

  return { systemOS, systemOSVersion };
}
